import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { ArrowLeft, X } from 'lucide-react';
import { emailReports } from '../../lib/api';

const RECENT_EMAILS_KEY = 'hislandtime.recentAccountantEmails';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

function getRecentEmails() {
    try { return JSON.parse(localStorage.getItem(RECENT_EMAILS_KEY)) || []; }
    catch { return []; }
}

function saveRecentEmail(email) {
    const updated = [email, ...getRecentEmails().filter(e => e !== email)].slice(0, 3);
    localStorage.setItem(RECENT_EMAILS_KEY, JSON.stringify(updated));
}

const MONTH_VALUES = [1,2,3,4,5,6,7,8,9,10,11,12];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

/**
 * EmailReportsDialog — two-step dialog for emailing monthly reports.
 *
 * Step 1: Select month/year and which attachments to include.
 * Step 2: Enter one or more recipient email addresses and an optional
 *         message, then send.
 *
 * Props:
 *   open        {boolean}  — controlled open state
 *   onOpenChange {function} — called with boolean when dialog should open/close
 *   defaultMonth {number}  — 1–12, the month pre-selected in Step 1
 *   defaultYear  {number}  — the year pre-selected in Step 1
 */
export default function EmailReportsDialog({ open, onOpenChange, defaultMonth, defaultYear }) {
    const { t } = useTranslation();

    const resolvedMonth = defaultMonth ?? new Date().getMonth() + 1;
    const resolvedYear  = defaultYear  ?? new Date().getFullYear();

    // ── Step state ──────────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);

    // ── Step 1 state ────────────────────────────────────────────────────────────
    const [selectedMonth, setSelectedMonth] = useState(resolvedMonth);
    const [selectedYear,  setSelectedYear]  = useState(resolvedYear);
    const [includeCustomerInvoices, setIncludeCustomerInvoices] = useState(true);
    const [includeCostCentres, setIncludeCostCentres] = useState(true);
    const [includeSalary,   setIncludeSalary]   = useState(true);
    const [includeReceipts, setIncludeReceipts] = useState(true);
    const [includeSupplierInvoices, setIncludeSupplierInvoices] = useState(true);

    const [language, setLanguage] = useState('en');

    // ── Step 2 state ────────────────────────────────────────────────────────────
    // Multi-recipient chip input — recipientEmails holds committed chips,
    // emailInputValue holds text the user is still typing.
    const [recipientEmails, setRecipientEmails] = useState([]);
    const [emailInputValue, setEmailInputValue] = useState('');
    const [recipientError, setRecipientError]   = useState('');
    const [message,        setMessage]        = useState('');
    const [sending,        setSending]        = useState(false);
    const [sendError,      setSendError]      = useState('');
    const [recentEmails,   setRecentEmails]   = useState([]);

    // Reset form when dialog opens / defaultMonth|Year changes
    useEffect(() => {
        if (open) {
            setStep(1);
            setSelectedMonth(defaultMonth ?? new Date().getMonth() + 1);
            setSelectedYear(defaultYear   ?? new Date().getFullYear());
            setIncludeCustomerInvoices(true);
            setIncludeCostCentres(true);
            setIncludeSalary(true);
            setIncludeReceipts(true);
            setIncludeSupplierInvoices(true);
            setLanguage('en');
            setRecipientEmails([]);
            setEmailInputValue('');
            setRecipientError('');
            setMessage('');
            setSending(false);
            setSendError('');
            setRecentEmails(getRecentEmails());
        }
    }, [open, defaultMonth, defaultYear]);

    // ── Derived ─────────────────────────────────────────────────────────────────
    const anyChecked = includeCustomerInvoices || includeSalary || includeReceipts || includeCostCentres || includeSupplierInvoices;

    // ── Recipient chip helpers ───────────────────────────────────────────────────
    const addRecipient = (raw) => {
        const email = raw.trim();
        if (!email) return;
        if (!EMAIL_REGEX.test(email)) {
            setRecipientError(t('email_dialog.invalid_email', { email }));
            return;
        }
        setRecipientEmails(prev => {
            if (prev.some(e => e.toLowerCase() === email.toLowerCase())) {
                setRecipientError('');
                return prev;
            }
            if (prev.length >= MAX_RECIPIENTS) {
                setRecipientError(t('email_dialog.max_recipients', { max: MAX_RECIPIENTS }));
                return prev;
            }
            setRecipientError('');
            return [...prev, email];
        });
        setEmailInputValue('');
    };

    const removeRecipient = (email) => {
        setRecipientEmails(prev => prev.filter(e => e !== email));
    };

    const commitPendingEmailInput = () => {
        if (emailInputValue.trim()) addRecipient(emailInputValue);
    };

    const handleEmailInputKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addRecipient(emailInputValue);
        } else if (e.key === 'Backspace' && !emailInputValue && recipientEmails.length > 0) {
            removeRecipient(recipientEmails[recipientEmails.length - 1]);
        }
    };

    // ── Handlers ────────────────────────────────────────────────────────────────
    const handleNext = () => {
        setSendError('');
        setStep(2);
    };

    const handleBack = () => {
        setSendError('');
        setStep(1);
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    const handleSend = async () => {
        setSendError('');

        // Commit any trailing typed email that wasn't chip-ified yet
        let finalRecipients = recipientEmails;
        const pending = emailInputValue.trim();
        if (pending) {
            if (!EMAIL_REGEX.test(pending)) {
                setSendError(t('email_dialog.invalid_email', { email: pending }));
                return;
            }
            if (!finalRecipients.some(e => e.toLowerCase() === pending.toLowerCase())) {
                if (finalRecipients.length >= MAX_RECIPIENTS) {
                    setSendError(t('email_dialog.max_recipients', { max: MAX_RECIPIENTS }));
                    return;
                }
                finalRecipients = [...finalRecipients, pending];
            }
        }
        if (finalRecipients.length === 0) {
            setSendError(t('email_dialog.recipients_required'));
            return;
        }

        setSending(true);
        try {
            const res = await emailReports({
                month:            selectedMonth,
                year:             selectedYear,
                include_billing:  false,
                include_customer_invoices: includeCustomerInvoices,
                include_cost_centres: includeCostCentres,
                include_salary:   includeSalary,
                include_receipts: includeReceipts,
                include_supplier_invoices: includeSupplierInvoices,
                recipient_email:  finalRecipients[0],
                recipient_emails: finalRecipients,
                message:          message,
                language:         language,
            });
            if (res.data?.status === 'ok') {
                finalRecipients.forEach(saveRecentEmail);
                toast.success(t('email_dialog.send_success'));
                onOpenChange(false);
            } else {
                setSendError(t('email_dialog.send_error'));
            }
        } catch {
            setSendError(t('email_dialog.send_error'));
        } finally {
            setSending(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                {step === 1 ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('email_dialog.title')}</DialogTitle>
                            <DialogDescription>
                                {t('email_dialog.description')}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-5 py-2">
                            {/* Month / Year selectors */}
                            <div className="flex gap-3">
                                <div className="flex-1 space-y-1.5">
                                    <Label htmlFor="email-reports-month">{t('email_dialog.month_label')}</Label>
                                    <Select
                                        value={String(selectedMonth)}
                                        onValueChange={(v) => setSelectedMonth(Number(v))}
                                    >
                                        <SelectTrigger id="email-reports-month">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MONTH_VALUES.map((m) => (
                                                <SelectItem key={m} value={String(m)}>
                                                    {t(`email_dialog.months.${m}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex-1 space-y-1.5">
                                    <Label htmlFor="email-reports-year">{t('email_dialog.year_label')}</Label>
                                    <Select
                                        value={String(selectedYear)}
                                        onValueChange={(v) => setSelectedYear(Number(v))}
                                    >
                                        <SelectTrigger id="email-reports-year">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {YEARS.map((y) => (
                                                <SelectItem key={y} value={String(y)}>
                                                    {y}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Report language */}
                            <div className="space-y-1.5">
                                <Label htmlFor="report-language">{t('email_dialog.language_label')}</Label>
                                <Select value={language} onValueChange={setLanguage}>
                                    <SelectTrigger id="report-language">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="en">{t('email_dialog.language_en')}</SelectItem>
                                        <SelectItem value="sv">{t('email_dialog.language_sv')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Attachment checkboxes */}
                            <div className="space-y-3">
                                <p className="text-sm font-medium">{t('email_dialog.attachments_label')}</p>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="include-customer-invoices"
                                        checked={includeCustomerInvoices}
                                        onCheckedChange={(checked) => setIncludeCustomerInvoices(!!checked)}
                                    />
                                    <Label htmlFor="include-customer-invoices" className="font-normal cursor-pointer">
                                        {t('email_dialog.include_customer_invoices')}
                                    </Label>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="include-salary"
                                        checked={includeSalary}
                                        onCheckedChange={(checked) => setIncludeSalary(!!checked)}
                                    />
                                    <Label htmlFor="include-salary" className="font-normal cursor-pointer">
                                        {t('email_dialog.include_salary')}
                                    </Label>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="include-receipts"
                                        checked={includeReceipts}
                                        onCheckedChange={(checked) => setIncludeReceipts(!!checked)}
                                    />
                                    <Label htmlFor="include-receipts" className="font-normal cursor-pointer">
                                        {t('email_dialog.include_receipts')}
                                    </Label>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="include-cost-centres"
                                        checked={includeCostCentres}
                                        onCheckedChange={(checked) => setIncludeCostCentres(!!checked)}
                                    />
                                    <Label htmlFor="include-cost-centres" className="font-normal cursor-pointer">
                                        {t('email_dialog.include_cost_centres')}
                                    </Label>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="include-supplier-invoices"
                                        checked={includeSupplierInvoices}
                                        onCheckedChange={(checked) => setIncludeSupplierInvoices(!!checked)}
                                    />
                                    <Label htmlFor="include-supplier-invoices" className="font-normal cursor-pointer">
                                        {t('email_dialog.include_supplier_invoices')}
                                    </Label>
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="ghost" onClick={handleClose}>
                                {t('email_dialog.cancel')}
                            </Button>
                            <Button onClick={handleNext} disabled={!anyChecked}>
                                {t('email_dialog.next')}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="rounded p-0.5 hover:bg-muted transition-colors"
                                    aria-label={t('email_dialog.back_aria')}
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                </button>
                                {t('email_dialog.title')}
                            </DialogTitle>
                            <DialogDescription>
                                {t('email_dialog.step2_description')}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            {/* Recipient email(s) — chip input, Enter/comma/blur commits a chip */}
                            <div className="space-y-1.5">
                                <Label htmlFor="accountant-email">{t('email_dialog.accountant_email_label')}</Label>
                                <div
                                    className="flex flex-wrap items-center gap-1.5 min-h-10 w-full rounded-md border border-input bg-background px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring"
                                    onClick={() => document.getElementById('accountant-email')?.focus()}
                                >
                                    {recipientEmails.map((email) => (
                                        <span
                                            key={email}
                                            className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                                        >
                                            {email}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); removeRecipient(email); }}
                                                className="rounded-full hover:bg-muted-foreground/20 min-h-[20px] min-w-[20px] inline-flex items-center justify-center"
                                                aria-label={t('email_dialog.remove_recipient', { email })}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        id="accountant-email"
                                        type="email"
                                        className="flex-1 min-w-[140px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                        placeholder={recipientEmails.length === 0 ? t('email_dialog.accountant_email_placeholder') : ''}
                                        value={emailInputValue}
                                        onChange={(e) => setEmailInputValue(e.target.value)}
                                        onKeyDown={handleEmailInputKeyDown}
                                        onBlur={commitPendingEmailInput}
                                    />
                                </div>
                                {recipientError && (
                                    <p className="text-xs text-destructive">{recipientError}</p>
                                )}
                                {recentEmails.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {recentEmails.map((email) => (
                                            <button
                                                key={email}
                                                type="button"
                                                onClick={() => addRecipient(email)}
                                                className="text-xs px-2 py-1 rounded-full border border-border bg-muted hover:bg-muted/70 transition-colors truncate max-w-[200px]"
                                                title={email}
                                            >
                                                {email}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Optional message */}
                            <div className="space-y-1.5">
                                <Label htmlFor="email-message">
                                    {t('email_dialog.message_label')}
                                </Label>
                                <Textarea
                                    id="email-message"
                                    placeholder={t('email_dialog.message_placeholder')}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    maxLength={500}
                                    rows={3}
                                />
                                <p className="text-xs text-muted-foreground text-right">
                                    {message.length}/500
                                </p>
                            </div>

                            {/* Inline error */}
                            {sendError && (
                                <p className="text-sm text-destructive">{sendError}</p>
                            )}
                        </div>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="ghost" onClick={handleClose} disabled={sending}>
                                {t('email_dialog.cancel')}
                            </Button>
                            <Button
                                onClick={handleSend}
                                disabled={sending || (recipientEmails.length === 0 && !emailInputValue.trim())}
                            >
                                {sending ? t('email_dialog.sending') : t('email_dialog.send')}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
